import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import { getDayPosition } from './getDayPosition';
import { useTimeTracking } from '../../time-tracking/AppTimeProvider';
import SmallProgressBar from '../../special/SmallProgressBar';
import { renderHours } from '../../../scenes/time-tracking/worktime';
import { ProjectUserStack } from '../project/ProjectUserStack';
import DateLabel from '../../label/DateLabel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import FlagIcon from '@mui/icons-material/Flag';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';

export const GantWP = ({
  workPackage,
  dayWidth,
  onEdit,
  onShow,
  days,
  inEditMode,
  onChange,
  projectId,
  resolution,
  allowedStatuses,
  editable,
  infoWidth,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const { icon, title, startDate, endDate, users, id, status } = workPackage;

  const startIndex = startDate
    ? getDayPosition(startDate, days) / resolution
    : 0;
  const endIndex = endDate
    ? (getDayPosition(endDate, days) + 1) / resolution
    : days.length / resolution;

  const offset = 7;

  const { clockIn, clockOut, activeProject, secondsIn } = useTimeTracking();

  const activeWorkpackage =
    activeProject &&
    activeProject.activeWorkPackage &&
    activeProject.activeWorkPackage.id === workPackage.id
      ? {
          ...activeProject.activeWorkPackage,
          totalTimeWorkedSeconds: secondsIn
            ? activeProject.activeWorkPackage.totalTimeWorkedSeconds + secondsIn
            : activeProject.activeWorkPackage.totalTimeWorkedSeconds,
        }
      : null;

  const workPercentage = activeWorkpackage
    ? activeWorkpackage.totalTimeWorkedSeconds / activeWorkpackage.estimatedTime
    : workPackage.totalTimeWorkedSeconds / workPackage.estimatedTime;

  const workedTime =
    (activeWorkpackage
      ? activeWorkpackage.totalTimeWorkedSeconds
      : workPackage.totalTimeWorkedSeconds) / 60;

  const statusMap = {
    0: {
      label: t('Running'),
      Icon: DirectionsRunIcon,
      color: colors.greenAccent[600],
      opacity: 1,
    },
    1: {
      label: t('Finished'),
      Icon: FlagIcon,
      color: colors.grey[600],
      opacity: 1,
    },
    2: {
      label: t('Infinite'),
      Icon: AllInclusiveIcon,
      color: colors.blueAccent[600],
      opacity: 1,
    },
    3: {
      label: t('Canceled'),
      Icon: ReportGmailerrorredIcon,
      color: colors.redAccent[600],
      opacity: 1,
    },
  };

  if (!allowedStatuses?.includes(status)) return;

  const Icon = statusMap[status].Icon;

  return (
    <>
      <Box
        onDoubleClick={() => {
          if (editable) onEdit(workPackage);
        }}
        onClick={(e) => onShow(e, workPackage)}
        sx={{
          bgcolor: colors.card,
          boxShadow: `0px 3px 7px rgba(0,0,0,0.2)`,
          marginLeft: `${startIndex * dayWidth + offset}px`,
          maxWidth: `${(endIndex - startIndex) * dayWidth - offset * 2}px`,
          width: `${(endIndex - startIndex) * dayWidth - offset * 2}px`,
          border: `1px solid ${statusMap[status]?.color}`,
          opacity: statusMap[status]?.opacity,
          ':hover': {
            bgcolor: colors.info + '30',
            border: `1px solid ${colors.info}80`,
          },
          '@container (min-width: 100px)': {
            '.team': {
              display: 'block',
            },
          },
          '@container (min-width: 280px)': {
            '.title': {
              display: 'flex',
            },
          },
          '@container (min-width: 380px)': {
            '.percentage': {
              display: 'block',
            },
          },
        }}
        className="@container flex items-center gap-2 rounded-lg px-2 backdrop-blur-md cursor-pointer justify-between"
      >
        <Box className="flex items-center">
          <img
            className="w-[50px] h-[45px] object-contain -ml-2"
            src={icon?.temporaryUrl}
          />
          <Box>
            <Box className="hidden title gap-2 items-center">
              <Typography fontWeight={600}>
                <Tooltip title={statusMap[status]?.label}>
                  <Icon
                    sx={{ color: statusMap[status]?.color }}
                    className="mr-1"
                  />
                </Tooltip>
                {title}
              </Typography>

              <NavLink
                to={`/projects/management/${projectId}/work-package/${id}`}
              >
                <IconButton size="small">
                  <OpenInNewIcon sx={{ fontSize: '14px' }} />
                </IconButton>
              </NavLink>
            </Box>
            <Box className="hidden @xs:block">
              {startDate || endDate ? (
                <Typography
                  className=" opacity-60"
                  fontSize={12}
                  fontWeight={500}
                >
                  {startDate ? <DateLabel date={startDate} /> : '??'}
                  {' - '}
                  {endDate ? <DateLabel date={endDate} /> : '??'}
                </Typography>
              ) : (
                <Typography
                  className=" opacity-60"
                  fontSize={12}
                  fontWeight={500}
                >
                  {t('permanently')}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Box
          className="gap-2 flex-col flex @sm:flex-row"
          sx={{
            '.MuiAvatar-root': {
              width: '30px',
              height: '30px',
            },
          }}
        >
          {Boolean(workPercentage) && workPercentage !== Infinity && (
            <Box className=" items-center gap-2 hidden @sm:flex">
              <SmallProgressBar percent={workPercentage} />
              <Box className="hidden percentage">
                <Typography fontWeight={600} fontSize={11}>
                  {Math.round(workPercentage * 100)}%
                </Typography>
                <Typography
                  fontSize={11}
                  fontWeight={500}
                  className="opacity-50"
                >
                  {renderHours(workedTime)}h
                  {Boolean(workPackage.estimatedTime) &&
                    ` / ${renderHours(workPackage.estimatedTime / 60)}h`}{' '}
                </Typography>
              </Box>
            </Box>
          )}
          <Box className="hidden team">
            <ProjectUserStack
              editmode={inEditMode}
              users={users}
              workPackage={workPackage}
              onChange={onChange}
              projectId={projectId}
              noLabel
              size="20px"
              noEmptyIcon
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};
